import React, { useState, useEffect } from 'react';
import { styled } from '@linaria/react';
import { useTheme, ITheme } from 'src/theme';

import tickWhiteIcon from '@assets/icon/confirmation-tick-white.svg';
import tickGreyIcon from '@assets/icon/confirmation-tick-grey.svg';

const HiddenCheckbox = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
`;

const IconElement = styled.img<CheckboxProps>`
  width: 18px;
  height: 15px;
  position: relative;
  object-fit: contain;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  top: 7px;
`;

const CircleElement = styled.div<CheckboxProps>`
  width: 16px;
  height: 16px;
  position: absolute;
  object-fit: contain;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  background: ${(props) => props.theme?.colors.grey[1100]};
  border-radius: 20px;
  z-index: 100;
  top: 6px;
  left: 6px;
`;

const StyledCheckbox = styled.div<CheckboxProps>`
  cursor: ${(prop) => (prop.disabled ? 'default' : 'pointer')};
  position: relative;
  padding: 0 5px;
  height: 28px;
  background: ${(props) =>
    props.checked ? props.theme?.colors.grey[1100] : '#fff'};
  border-radius: ${(props) => (props.type === 'radio' ? '30px' : '4px')};
  transition: all 150ms;
  border: 1px solid
    ${(props) =>
      props.checked ? 'transparent' : props.theme?.colors.grey[100]};
  ${IconElement} {
    visibility: ${(props) =>
      props.checked
        ? props.type !== 'radio'
          ? 'visible'
          : 'hidden'
        : 'hidden'};
  }
  ${CircleElement} {
    visibility: ${(props) =>
      props.checked && props.type === 'radio' ? 'visible' : 'hidden'};
  }
  box-shadow: ${(props) =>
    props.checked ? 'none' : 'inset 0 2px 2px 0 rgba(204, 204, 204, 0.5)'};
  text-align: center;
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
`;

const Content = styled.div<CheckboxProps>`
  font-size: 16px;
  margin-left: 14px;
  line-height: 24px;
  transform: ${(props) =>
    props.variant === 'border' ? 'translateY(4px)' : 'translateY(2px)'};
  & p {
    color: ${(props) => props.theme?.colors.grey[500]};
    font-weight: ${(props) =>
      props.checked && props.variant === 'border' ? 'bold' : 'normal'};
  }
`;

const Container = styled.div<CheckboxProps>`
  display: flex;
  vertical-align: middle;
  padding: ${(props) => (props.variant === 'border' ? 8 : 0)}px;
  border: 1px solid ${(props) =>
    props.variant === 'border'
      ? props.checked
        ? props.theme?.colors.grey[1100]
        : props.theme?.colors.grey[100]
      : 'none'};
  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 3px green;
  }
  ${Content} p {
    color: ${(props) =>
      props.variant === 'border' && props.checked
        ? props.theme?.colors.white
        : props.theme?.colors.grey[500]};
  }
  ${StyledCheckbox} {
    background: ${(props) =>
      props.variant === 'border' && props.checked
        ? props.theme?.colors.white || '#fff'
        : props.checked
        ? props.theme?.colors.grey[1100]
        : '#fff'};
  }
  ${Content} p {
    color: ${(props) =>
      props.variant === 'border' && props.checked
        ? props.theme?.colors.white
        : props.theme?.colors.grey[500]};
  }
  border-radius: 4px;
  background-color: ${(props) =>
    props.variant === 'border'
      ? props.checked
        ? props.theme?.colors.grey[1100]
        : props.theme?.colors.grey[200]
      : 'unset'};
  margin: ${(props) =>
    typeof props.margin === 'number'
      ? `${props.margin || 0}px`
      : props.margin || 0};
  box-shadow: ${(props) =>
    props.variant === 'border'
      ? props.checked
        ? 'none'
        : '0 2px 2px 0 rgba(204, 204, 204, 0.5)'
      : 'none'};
  cursor: ${(props) => (props.variant === 'border' ? 'pointer' : 'default')};
  transition: background-color .5s,border-color .5s;
  @media screen and (max-width: 768px) {
    display: block;
    position: relative;
    padding-left: 40px;
    ${StyledCheckbox} {
      position: absolute;
      width: 18px;
      top: 50%;
      left: 10px
      transform: translateY(-50%);
      margin-bottom: 14px;
    }
    ${Content} {
      transform: none;
    }
  }
`;

export interface CheckboxProps {
  variant?: 'default' | 'border';
  type?: 'default' | 'radio';
  theme?: ITheme;
  className?: string;
  checked?: boolean;
  children?: React.ReactNode;
  margin: number | string;
  name?: string;
  disabled?: boolean;
  onChange?: (e: any) => void;
}

function Checkbox({
  variant,
  children,
  checked,
  margin,
  onChange,
  name,
  disabled = false,
  className,
  ...props
}: CheckboxProps) {
  const [value, change] = useState<boolean | undefined>(checked);
  const handleChange = (e: any) => {
    if (disabled) return;
    const nextValue = !value;
    change(nextValue);
    if (onChange) {
      onChange({ ...e, target: { ...e.target, name, value: nextValue } });
    }
  };

  useEffect(() => {
    if (checked !== undefined && value !== checked) {
      change(checked);
    }
  }, [value, checked]);

  const theme: ITheme = useTheme();
  return (
    <Container
      className={className}
      theme={theme}
      variant={variant}
      checked={value}
      margin={margin}
      onClick={variant === 'border' ? handleChange : undefined}
    >
      <HiddenCheckbox />
      <StyledCheckbox
        className="checkbox"
        name={name}
        theme={theme}
        disabled={disabled}
        variant={variant}
        checked={value}
        {...props}
        onClick={handleChange}
        margin={margin}
      >
        <IconElement
          src={variant === 'border' ? tickGreyIcon : tickWhiteIcon}
          margin={margin}
        ></IconElement>
        <CircleElement margin={margin} theme={theme}></CircleElement>
      </StyledCheckbox>
      <Content theme={theme} checked={value} variant={variant} margin={margin}>
        {children}
      </Content>
    </Container>
  );
}

Checkbox.defaultProps = {
  variant: 'default',
  margin: '0 0 15px',
  onChange: () => {},
};

export default Checkbox;
